import type { Answers, ConcernKey, EvidenceItem, TSuppressionProfile } from "./_schema";
import { CONCERN_KEYS } from "./_schema";
import { deriveBoostProfile, type ExtraSignals } from "./boost-profile";

/**
 * Maps the quiz-v7 `concern` answer onto the legacy `results-want` answer space
 * so the existing expectedOutcome lookup in `deriveBoostProfile` returns
 * cohort-relevant copy without authoring new entries. `weight-loss` has no
 * direct legacy equivalent; map to `all-of-above` (the full-reset framing is
 * the best fit for the skinny-fat pattern).
 */
const CONCERN_TO_RESULTS_WANT: Record<ConcernKey, string> = {
  energy: "energy",
  muscle: "muscle",
  "sex-drive": "sex-drive",
  focus: "focus",
  "weight-loss": "all-of-above",
};

const ENERGY_COST_LABEL: Record<string, string> = {
  "skip-gym": "Skipped workouts",
  "short-fuse": "Short-tempered",
  "caffeine-crutch": "Caffeine reliance",
  coasting: "Coasting at work",
};
const STRENGTH_TREND_LABEL: Record<string, string> = {
  "losing-ground": "Going backwards",
  stuck: "Plateaued",
  "slow-gains": "Slowing gains",
  progressing: "Progressing",
};
const LIBIDO_LABEL: Record<string, string> = {
  "much-lower": "Much lower",
  "somewhat-lower": "Somewhat lower",
  inconsistent: "Inconsistent",
  strong: "Still strong",
};
const FOG_FREQUENCY_LABEL: Record<string, string> = {
  daily: "Daily",
  often: "Several/week",
  sometimes: "Sometimes",
  rarely: "Rarely",
};
const WEIGHT_WHERE_LABEL: Record<string, string> = {
  belly: "Belly & midsection",
  "love-handles": "Love handles",
  "all-over": "All over",
  chest: "Chest & face",
};

const COHORT_PRIORITY: Record<
  ConcernKey,
  { id: string; label: string; map: Record<string, string> }
> = {
  energy: { id: "energy-cost", label: "Cost", map: ENERGY_COST_LABEL },
  muscle: { id: "strength-trend", label: "Lifts", map: STRENGTH_TREND_LABEL },
  "sex-drive": { id: "libido-change", label: "Drive", map: LIBIDO_LABEL },
  focus: { id: "fog-frequency", label: "Fog", map: FOG_FREQUENCY_LABEL },
  "weight-loss": { id: "weight-where", label: "Holds", map: WEIGHT_WHERE_LABEL },
};

function isConcernKey(v: unknown): v is ConcernKey {
  return typeof v === "string" && (CONCERN_KEYS as readonly string[]).includes(v);
}

/**
 * Maps quiz-v7's cohort gradient answers onto severity tiers. These questions
 * (strength-trend, recovery, fog-frequency, libido-change, morning-drive) are
 * NOT read by deriveBoostProfile, so without this they contribute zero severity
 * and cohorts whose branch never asks energy-3pm could never escalate past the
 * floor. Worst answer -> tier1 (highest correlation with measured low T);
 * second-worst -> tier2. Milder answers contribute nothing.
 *
 * Cohort-severity coverage by branch:
 *   muscle    -> strength-trend + recovery   (2 cohort signals)
 *   sex-drive -> libido-change + morning-drive (2)
 *   focus     -> fog-frequency               (1)
 *   energy    -> none (severity rides on energy-3pm, which the base deriver reads)
 *   weight-loss -> none (severity rides on body-composition / processed-foods)
 * "Where it hurts" answers (weight-where, focus-cost, energy-cost) are
 * intentionally excluded — they inform evidence/root-cause, not severity. So
 * energy and weight-loss escalate only through the shared diagnostic tail; this
 * is deliberate, not an omission.
 */
const TIER1_COHORT: Record<string, readonly string[]> = {
  "libido-change": ["much-lower"],
  "morning-drive": ["rarely"],
  "strength-trend": ["losing-ground"],
  recovery: ["wrecked"],
  "fog-frequency": ["daily"],
};
const TIER2_COHORT: Record<string, readonly string[]> = {
  "libido-change": ["somewhat-lower"],
  "morning-drive": ["sometimes"],
  "strength-trend": ["stuck"],
  recovery: ["slow"],
  "fog-frequency": ["often"],
};

/**
 * quiz-v7 asks stress as a 4-step frequency scale; deriveBoostProfile reads
 * `stress-symptoms` as a binary ("yes" / "no") across severity, root-cause, QoL,
 * decline-pattern, and the evidence chip. Collapse the worst two steps to "yes"
 * (today's high-stress behaviour) and the milder two to "no", so the diagnostic
 * weighting is byte-identical to the old yes/no question — only the
 * self-admission UX improves (no binary "No" escape hatch). Same pre-map seam as
 * `CONCERN_TO_RESULTS_WANT`: it normalizes the INPUT, never the base deriver.
 * Returns null for any value already in the binary space (legacy / test inputs)
 * or unknown, so those pass through to the base deriver untouched.
 */
const STRESS_SCALE_TO_BINARY: Record<string, "yes" | "no"> = {
  constant: "yes",
  "most-days": "yes",
  "some-days": "no",
  rarely: "no",
};

function mapStressToBinary(v: unknown): "yes" | "no" | null {
  if (typeof v === "string" && v in STRESS_SCALE_TO_BINARY) {
    return STRESS_SCALE_TO_BINARY[v]!;
  }
  return null;
}

function cohortSignals(answers: Answers): ExtraSignals {
  let tier1 = 0;
  let tier2 = 0;
  for (const [id, values] of Object.entries(TIER1_COHORT)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) tier1 += 1;
  }
  for (const [id, values] of Object.entries(TIER2_COHORT)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) tier2 += 1;
  }
  return { tier1, tier2 };
}

function injectCohortChip(
  evidence: readonly EvidenceItem[],
  answers: Answers,
  concern: ConcernKey,
): EvidenceItem[] {
  const cfg = COHORT_PRIORITY[concern];
  const v = answers[cfg.id];
  if (typeof v !== "string") return [...evidence];
  const value = cfg.map[v];
  if (!value) return [...evidence];
  return [{ label: cfg.label, value }, ...evidence].slice(0, 4);
}

/**
 * Quiz-v7 deriver. Wraps `deriveBoostProfile` without modifying it. Four
 * additions over the base deriver:
 *   1. concern → results-want pre-map (keeps expectedOutcome cohort-relevant)
 *   2. cohort gradient answers → severity, via the deriver's `extraSignals`
 *      seam (the cohort questions are invisible to the base deriver otherwise)
 *   3. cohort-priority chip in evidence slot 0 (cohort-signal surfaces first)
 *   4. cohort field stamp (Result/Projection/Offer cohort overlays)
 *
 * `deriveBoostProfile` is never asked to do anything cohort-aware — additions
 * wrap its input (pre-map), feed it through the severity-only `extraSignals`
 * seam, or wrap its output. The Boost invariance test in
 * `tests/quizzes/boost-profile.test.ts` asserts the base deriver never sets
 * cohort and is byte-identical when called without `extraSignals`.
 */
export function deriveQuizV7Profile(answers: Answers): TSuppressionProfile {
  const concern = answers["concern"];
  const knownConcern = isConcernKey(concern) ? concern : null;

  // Pre-map quiz-v7's richer answers into the space deriveBoostProfile reads:
  // (1) concern -> results-want (cohort-relevant expectedOutcome), and
  // (2) the 4-step stress scale -> the binary stress-symptoms the base deriver
  // keys on. Both normalize the INPUT only; the base deriver is never modified.
  let adjusted: Answers = answers;
  if (!("results-want" in answers) && knownConcern) {
    adjusted = { ...adjusted, "results-want": CONCERN_TO_RESULTS_WANT[knownConcern] };
  }
  const stressBinary = mapStressToBinary(answers["stress-symptoms"]);
  if (stressBinary) {
    adjusted = { ...adjusted, "stress-symptoms": stressBinary };
  }

  const baseProfile = deriveBoostProfile(adjusted, { extraSignals: cohortSignals(answers) });

  if (!knownConcern) return baseProfile;

  return {
    ...baseProfile,
    evidence: injectCohortChip(baseProfile.evidence, answers, knownConcern),
    cohort: knownConcern,
  };
}
